import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Bot,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Pause,
  Play,
  Plus,
  Radio,
  RefreshCw,
  ShieldCheck,
  X
} from "lucide-react";
import { client, RequestError, type SourceSubscription } from "./api";

type SourceStatus = "ACTIVE" | "PAUSED" | "DEGRADED" | "ERROR" | "CONNECTING";
type ConnectorType = "PUBLIC_CHANNEL" | "BOT_TO_BOT" | "ADMIN_FORWARD";

type CreateSourceInput = {
  displayName: string;
  telegramHandle: string;
  connectorType: ConnectorType;
};
type SourceTestResult = { ok: boolean; identity?: string; message?: string };

const connectorLabels: Record<ConnectorType, string> = {
  PUBLIC_CHANNEL: "공개 채널",
  BOT_TO_BOT: "Bot-to-Bot",
  ADMIN_FORWARD: "관리자 전달"
};

const statusLabels: Record<SourceStatus, string> = {
  ACTIVE: "수집 중",
  PAUSED: "일시정지",
  DEGRADED: "확인 필요",
  ERROR: "연결 오류",
  CONNECTING: "연결 중"
};

export default function SourcesPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [showPaused, setShowPaused] = useState(true);
  const query = useQuery({
    queryKey: ["source-subscriptions"],
    queryFn: () => client.sourceSubscriptions(),
    refetchInterval: 30_000
  });

  const sources = useMemo(
    () => (query.data?.sources ?? []).filter((source) => showPaused || source.status !== "PAUSED"),
    [query.data?.sources, showPaused]
  );
  const activeCount = query.data?.sources.filter((source) => source.status === "ACTIVE").length ?? 0;
  const pendingCount = query.data?.sources.reduce((sum, source) => sum + source.pendingReviewCount, 0) ?? 0;

  return (
    <>
      <header className="page-header source-page-header">
        <div>
          <p className="eyebrow">Telegram 수집</p>
          <h1>외부 소스</h1>
          <p>구독한 채널과 봇의 새 게시물을 검토 대기함으로 수집합니다.</p>
        </div>
        <div className="page-actions">
          <button className="button primary" type="button" onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden="true" />새 소스
          </button>
        </div>
      </header>

      <section className="manual-review-notice" aria-label="수동 승인 정책">
        <span className="manual-review-icon"><ShieldCheck aria-hidden="true" /></span>
        <div>
          <strong>자동 재게시는 사용하지 않습니다</strong>
          <p>수집된 모든 항목은 검토 대기함에만 들어갑니다. 운영자가 내용을 확인하고 명시적으로 승인해야 게시됩니다.</p>
        </div>
      </section>

      {!query.isLoading && query.data?.configured === false && <section className="source-setup-required" role="alert"><CircleAlert /><div><strong>수집 연결 설정이 필요합니다</strong><p>공개 채널용 Telegram 사용자 세션 또는 ADMIN 봇을 먼저 연결하세요.</p></div><a className="button secondary" href="/settings#settings-telegram">Telegram 설정</a></section>}

      <div className="source-summary" aria-label="소스 현황">
        <div><Radio aria-hidden="true" /><span>수집 중<strong>{activeCount}</strong></span></div>
        <div><Clock3 aria-hidden="true" /><span>검토 대기<strong>{pendingCount}</strong></span></div>
        <label className="source-paused-toggle">
          <input type="checkbox" checked={showPaused} onChange={(event) => setShowPaused(event.target.checked)} />
          <span>일시정지 포함</span>
        </label>
      </div>

      {query.isLoading && <SourceState loading title="소스를 불러오는 중" />}
      {query.error && (
        <SourceState
          error
          title="소스를 불러오지 못했습니다"
          body={messageOf(query.error)}
          action={<button className="button secondary" onClick={() => void query.refetch()}>다시 시도</button>}
        />
      )}
      {!query.isLoading && !query.error && sources.length === 0 && (
        <SourceState
          title={query.data?.sources.length ? "표시할 활성 소스가 없습니다" : "아직 구독한 소스가 없습니다"}
          body="새 소스를 연결하면 게시물이 자동 게시되지 않고 검토 대기함으로 수집됩니다."
          action={<button className="button primary" onClick={() => setCreateOpen(true)}>첫 소스 연결</button>}
        />
      )}
      {sources.length > 0 && (
        <section className="source-list" aria-label="Telegram 외부 소스 목록">
          {sources.map((source) => <SourceRow key={source.id} source={source} />)}
        </section>
      )}

      {createOpen && (
        <CreateSourceSheet
          onClose={() => setCreateOpen(false)}
          onCreated={async () => {
            setCreateOpen(false);
            await queryClient.invalidateQueries({ queryKey: ["source-subscriptions"] });
          }}
        />
      )}
    </>
  );
}

function SourceRow({ source }: { source: SourceSubscription }) {
  const queryClient = useQueryClient();
  const [testResult, setTestResult] = useState<SourceTestResult | null>(null);
  const nextStatus = source.status === "PAUSED" ? "ACTIVE" : "PAUSED";
  const update = useMutation({
    mutationFn: () => client.updateSourceSubscription(source.id, { status: nextStatus }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["source-subscriptions"] })
  });
  const test = useMutation({
    mutationFn: () => client.testSourceConnection(source.id),
    onSuccess: (result) => setTestResult(result)
  });

  return (
    <article className="source-row">
      <div className={`source-avatar connector-${source.connectorType.toLowerCase()}`} aria-hidden="true">
        {source.connectorType === "BOT_TO_BOT" ? <Bot /> : <Activity />}
      </div>
      <div className="source-main">
        <div className="source-title-line">
          <strong>{source.displayName}</strong>
          <SourceStatusBadge status={source.status} />
        </div>
        <span className="source-handle">{source.telegramHandle}</span>
        <div className="source-meta">
          <span>{connectorLabels[source.connectorType]}</span>
          <span>마지막 수집 {formatLastCollected(source.lastCollectedAt)}</span>
          <span>누적 {source.collectedCount.toLocaleString("ko-KR")}건</span>
          {source.pendingReviewCount > 0 && <strong>검토 대기 {source.pendingReviewCount}건</strong>}
        </div>
        {source.lastError && <p className="source-error"><CircleAlert aria-hidden="true" />{source.lastError}</p>}
        {testResult && (
          <p className={testResult.ok ? "source-test-result ok" : "source-test-result failed"} role="status">
            {testResult.ok ? <CheckCircle2 aria-hidden="true" /> : <CircleAlert aria-hidden="true" />}
            {testResult.message ?? (testResult.ok ? `${testResult.identity ?? "소스"} 연결을 확인했습니다.` : "연결을 확인하지 못했습니다.")}
          </p>
        )}
        {(update.error || test.error) && <p className="source-error"><CircleAlert aria-hidden="true" />{messageOf(update.error ?? test.error)}</p>}
      </div>
      <div className="source-actions">
        <button className="button secondary" type="button" disabled={test.isPending} onClick={() => test.mutate()}>
          <RefreshCw className={test.isPending ? "is-spinning" : ""} aria-hidden="true" />
          {test.isPending ? "확인 중" : "연결 확인"}
        </button>
        <button className="button ghost" type="button" disabled={update.isPending} onClick={() => update.mutate()}>
          {nextStatus === "PAUSED" ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
          {update.isPending ? "변경 중" : nextStatus === "PAUSED" ? "일시정지" : "수집 재개"}
        </button>
      </div>
    </article>
  );
}

function CreateSourceSheet({ onClose, onCreated }: { onClose: () => void; onCreated: () => Promise<void> }) {
  const [form, setForm] = useState<CreateSourceInput>({
    displayName: "",
    telegramHandle: "",
    connectorType: "PUBLIC_CHANNEL"
  });
  const create = useMutation({ mutationFn: () => client.createSourceSubscription(form), onSuccess: onCreated });
  const handleIsValid = form.connectorType === "ADMIN_FORWARD" || /^(@[A-Za-z][A-Za-z0-9_]{3,}|-?\d{5,})$/.test(form.telegramHandle.trim());
  const canSubmit = form.displayName.trim().length > 0 && handleIsValid && !create.isPending;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="modal-layer" role="presentation">
      <button className="modal-scrim" type="button" aria-label="새 소스 창 닫기" onClick={onClose} />
      <section className="modal source-create-sheet" role="dialog" aria-modal="true" aria-labelledby="source-create-title">
        <header>
          <div>
            <p className="eyebrow">Telegram 연결</p>
            <h2 id="source-create-title">새 외부 소스</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="닫기"><X /></button>
        </header>
        <div className="modal-body">
          <div className="connector-picker" role="radiogroup" aria-label="수집 방식">
            <ConnectorOption
              checked={form.connectorType === "PUBLIC_CHANNEL"}
              title="공개 채널"
              body="공개 @username 채널을 서버 커넥터로 읽습니다."
              onSelect={() => setForm({ ...form, connectorType: "PUBLIC_CHANNEL" })}
            />
            <ConnectorOption
              checked={form.connectorType === "BOT_TO_BOT"}
              title="Bot-to-Bot"
              body="연결한 MTProto 사용자 계정이 먼저 대화를 시작한 bot의 메시지를 읽습니다."
              onSelect={() => setForm({ ...form, connectorType: "BOT_TO_BOT" })}
            />
            <ConnectorOption
              checked={form.connectorType === "ADMIN_FORWARD"}
              title="관리자 전달"
              body="허용된 운영자가 관리자 봇으로 전달하는 메시지의 원출처를 추적합니다."
              onSelect={() => setForm({ ...form, connectorType: "ADMIN_FORWARD" })}
            />
          </div>

          <label className="field">
            <span>관리 화면 표시명</span>
            <input
              autoFocus
              value={form.displayName}
              placeholder="예: 정치권 공개 채널"
              onChange={(event) => setForm({ ...form, displayName: event.target.value })}
            />
          </label>
          {form.connectorType === "ADMIN_FORWARD" ? (
            <div className="manual-review-inline"><ShieldCheck aria-hidden="true" /><span>연결된 <strong>zrs_admin_bot</strong>으로 전달한 게시물을 수집합니다. 별도 채널 ID는 필요하지 않습니다.</span></div>
          ) : (
            <label className="field">
              <span>채널 @username 또는 Chat ID</span>
              <input
                value={form.telegramHandle}
                inputMode="text"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="@channel_name"
                aria-describedby="source-handle-help"
                onChange={(event) => setForm({ ...form, telegramHandle: event.target.value })}
              />
              <small id="source-handle-help">비공개 채널은 먼저 수집 계정에 읽기 권한을 부여해야 합니다.</small>
            </label>
          )}

          <div className="manual-review-inline"><ShieldCheck aria-hidden="true" /><span>연결 후 들어오는 게시물은 모두 수동 승인 대기 상태로 저장됩니다.</span></div>
          {create.error && <p className="source-error"><CircleAlert aria-hidden="true" />{messageOf(create.error)}</p>}

          <div className="modal-actions">
            <button className="button secondary" type="button" onClick={onClose}>취소</button>
            <button className="button primary" type="button" disabled={!canSubmit} onClick={() => create.mutate()}>
              {create.isPending ? "연결 중…" : "연결하고 수집 시작"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function ConnectorOption({ checked, title, body, onSelect }: { checked: boolean; title: string; body: string; onSelect: () => void }) {
  return (
    <button className={`connector-option ${checked ? "selected" : ""}`} type="button" role="radio" aria-checked={checked} onClick={onSelect}>
      <span className="connector-radio" aria-hidden="true" />
      <span><strong>{title}</strong><small>{body}</small></span>
    </button>
  );
}

function SourceStatusBadge({ status }: { status: SourceStatus }) {
  return <span className={`source-status status-${status.toLowerCase()}`}><span aria-hidden="true" />{statusLabels[status]}</span>;
}

function SourceState({ title, body, action, loading, error }: { title: string; body?: string; action?: React.ReactNode; loading?: boolean; error?: boolean }) {
  return (
    <section className="source-state">
      {loading ? <span className="spinner" aria-hidden="true" /> : error ? <CircleAlert aria-hidden="true" /> : <Radio aria-hidden="true" />}
      <h2>{title}</h2>
      {body && <p>{body}</p>}
      {action}
    </section>
  );
}

function formatLastCollected(value: string | null): string {
  if (!value) return "없음";
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) return "확인 불가";
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return "방금";
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  return new Date(timestamp).toLocaleString("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function messageOf(error: unknown): string {
  return error instanceof RequestError || error instanceof Error ? error.message : "알 수 없는 오류입니다.";
}
